import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Badge, Box, Flex, Grid, Link, Spinner } from "theme-ui";
import { useNavigate, useParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { deleteProvider, getProvider, updateProvider } from "api/Providers";
import { CountryStates } from "helpers/CountryStates";
import { filterByKeys } from "helpers/default";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { BackArrowIcon, CheckMarkIcon, EllipsisIcon, QuestionMarkIcon, TrashIcon } from "components/svg";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { themevals } from "theme/themevals";
import Separator from "components/ui/Separator";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import TableData from "components/ui/TableData";
import { deleteDepot, getDepots, restoreDepot } from "api/Providers/Depots";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import CreateDepot from "./Depots/createModal";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";

const columnHelper = createColumnHelper();

const createColumn = (id: string, title: string, accessor: (row: any) => any, cell?: (info: any) => any, options = {}) =>
  columnHelper.accessor(accessor, { id, cell: cell || ((info) => info.getValue()), header: () => <span>{title}</span>, ...options });

export default function ProviderEdit() {
  const countryStates: any[] = Object.entries(CountryStates["US"]?.states || {}).map(([key, value]) => ({ value: key, label: value.name }));

  const navigate = useNavigate();
  const params = useParams();
  const toast = useToast();

  const [provider, setProvider] = useState<any>(null);
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  const submitUpdate = async (values: any) => {
    const previous = provider;
    const changedFields: string[] = [];

    const isEqual = (a: any, b: any) => {
      if (a === b) return true;
      if (typeof a === "undefined" || a === null) {
        return typeof b === "undefined" || b === null;
      }
      if (typeof b === "undefined" || b === null) {
        return false;
      }
      try {
        return JSON.stringify(a) === JSON.stringify(b);
      } catch {
        return false;
      }
    };

    const keys = ["name", "phone", "address", "contactEmail", "billingEmail"];
    const update = filterByKeys(keys, values);

    const res = await updateProvider({ data: { id: params.id }, update });
    if (res?.id) {
      toast.add("Provider updated.", "var(--theme-ui-colors-success)");

      if (!isEqual(previous?.name, res?.name)) {
        changedFields.push("name");
      }

      if (!isEqual(previous?.phone, res?.phone)) {
        changedFields.push("phone");
      }

      const prevAddress = previous?.address || {};
      const nextAddress = res?.address || {};
      const addressKeys = ["line1", "line2", "city", "state", "postalCode", "zip"];
      addressKeys.forEach((key) => {
        if (!isEqual(prevAddress?.[key], nextAddress?.[key])) {
          changedFields.push(`address.${key}`);
        }
      });

      if (!isEqual(previous?.contactEmail, res?.contactEmail)) {
        changedFields.push("contactEmail");
      }

      if (!isEqual(previous?.billingEmail, res?.billingEmail)) {
        changedFields.push("billingEmail");
      }

      if (changedFields.length > 0) {
        void trackActivity({
          module: "providers",
          action: "update_provider",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "provider",
          entityId: res?.id ?? params.id ?? null,
          metadata: {
            changedFields,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    } else {
      toast.add(res?.message ? res.message : "Error updating provider", "var(--theme-ui-colors-danger)");
    }
  };

  const _validate = Yup.object().shape({
    name: Yup.string().required("Provider name is required."),
  });

  const initProvider = async () => {
    const res = await getProvider({ data: { id: params.id } });
    setProvider(res?.id ? res : null);
  };

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const confirmDelete = async () => {
    setConfirmSubmit(true);
    const res = await deleteProvider({ data: provider });
    if (res?.success) {
      toast.add("Provider deactivated.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "providers",
        action: "deactivate_provider",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "provider",
        entityId: provider?.id ?? null,
        metadata: {
          source: "provider_detail",
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      navigate("/providers");
    } else {
      toast.add(res?.message ? res.message : "Error deleting provider.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const submitDelete = async () => {
    setConfirmModal(
      <BasicModal
        title="Deactivate provider?"
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        actions={
          <BasicButton type="button" scheme="danger" onClick={() => confirmDelete()} $submitting={confirmSubmit} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
            {!confirmSubmit && <TrashIcon fill="#fff" />}
            {"Deactivate"}
          </BasicButton>
        }
      >
        <P>
          Are you sure you want to deactivate <strong style={{ color: "var(--theme-ui-colors-black)" }}>{provider?.name}</strong>?
        </P>
      </BasicModal>
    );
  };

  const [deletingDepot, setDeletingDepot] = useState<boolean>(false);
  const [refreshDepots, setRefreshDepots] = useState<boolean>(false);

  const confirmDeleteDepot = async (del: any) => {
    setDeletingDepot(true);
    const res: any = await deleteDepot({ provider, data: del });
    if (res?.success) {
      toast.add("Depot deactivated.", "var(--theme-ui-colors-success)");
      setRefreshDepots(true);
      setTimeout(() => {
        setRefreshDepots(false);
      }, 100);
    } else {
      toast.add(res?.message ? res.message : "Error deleting provider.", "var(--theme-ui-colors-danger)");
    }
    setDeletingDepot(false);
    setConfirmModal(false);
  };

  const deleteDepotModal = async (del?: any) => {
    setConfirmModal(
      <BasicModal
        title="Deactivate depot?"
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
            <BasicButton type="button" scheme="danger" onClick={() => confirmDeleteDepot(del)} $submitting={deletingDepot} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
              {!deletingDepot && <TrashIcon fill={"#fff"} />}
              {"Deactivate"}
            </BasicButton>
        }
      >
        <P>
          Are you sure you want to deactivate depot <strong style={{ color: "var(--theme-ui-colors-black)" }}>{del?.name}</strong>?
        </P>
      </BasicModal>
    );
  };

  const confirmReactivateDepot = async (depotItem: any) => {
    setConfirmSubmit(true);
    try {
      const res: any = await restoreDepot({ provider, data: depotItem });
      if (res?.success) {
        toast.add("Depot reactivated.", "var(--theme-ui-colors-success)");
        void trackActivity({
          module: "providers",
          action: "reactivate_depot",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "depot",
          entityId: depotItem?.id ?? null,
          metadata: {
            providerId: provider?.id ?? null,
            source: "provider_edit",
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        setRefreshDepots(true);
        setTimeout(() => {
          setRefreshDepots(false);
        }, 100);
      } else {
        toast.add(res?.message ? res.message : "Error reactivating depot.", "var(--theme-ui-colors-danger)");
      }
    } catch (error: any) {
      toast.add(error?.message ? error.message : "Error reactivating depot.", "var(--theme-ui-colors-danger)");
    } finally {
      setConfirmSubmit(false);
      setConfirmModal(false);
    }
  };

  const reactivateDepotModal = async (depotItem?: any) => {
    setConfirmModal(
      <BasicModal
        title="Reactivate depot?"
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton
            type="button"
            onClick={() => confirmReactivateDepot(depotItem)}
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{
              margin: "0",
              backgroundColor: themevals.colors.success,
              borderColor: themevals.colors.success,
              color: "#fff",
            }}
          >
            {!confirmSubmit && <CheckMarkIcon fill={"#fff"} />}
            Reactivate
          </BasicButton>
        }
      >
        <P>
          Are you sure you want to reactivate depot <strong style={{ color: "var(--theme-ui-colors-black)" }}>{depotItem?.name}</strong>?
        </P>
      </BasicModal>
    );
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Provider";
    initProvider();
  }, []);

  useEffect(() => {
    if (!provider?.id || hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    void trackActivity({
      module: "providers",
      action: "view_provider_edit",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "provider",
      entityId: provider.id,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [provider?.id, user?.id, user?.company?.id]);

  if (!provider)
    return (
      <Box sx={{ textAlign: "center", paddingY: "1.5rem" }}>
        <Spinner />
      </Box>
    );

  return (
    <>
      <Header></Header>
      <Page
        title="Edit provider"
        subtitle="Edit your provider with updated details."
        backAction={
          <Link variant="back" href="/providers">
            <BackArrowIcon />
            Back to Providers
          </Link>
        }
      >
        <Formik initialValues={{ ...provider }} onSubmit={submitUpdate} validationSchema={_validate}>
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
            <Form noValidate autoComplete="off">
              <Tabs
                vertical
                stickyFooter
                footer={
                  <Flex sx={{ gap: "10px", flexGrow: 1 }}>
                    <BasicButton scheme="dangerSecondary" styles={{ marginRight: "auto!important", marginLeft: "10px" }} type="button" onClick={() => submitDelete()}>
                      <TrashIcon fill={themevals.colors.danger} /> Deactivate
                    </BasicButton>
                    <Link href="/providers" sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: "0 10px 0 0" }}>
                      Cancel
                    </Link>
                    <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 10px 0 0" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                      {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                      Save Provider
                    </BasicButton>
                  </Flex>
                }
              >
                <Tab itemKey="general">
                  <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                        Basic Info
                      </H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                        Edit Provider Name and Phone.
                      </Subtext>
                    </Box>
                    <Box>
                      <Flex sx={{ gap: "1rem" }}>
                        <TextInput
                          name="name"
                          label="Provider Name"
                          value={values.name}
                          onChange={(val: any) => setFieldValue("name", val)}
                          $errors={errors.name && submitCount > 0 ? errors.name : null}
                          $customStyles={{ flexGrow: 2 }}
                        />
                        <TextInput
                          name="phone"
                          label="Phone"
                          value={values.phone}
                          onChange={(val: any) => setFieldValue("phone", val)}
                          $errors={errors?.phone && submitCount > 0 ? errors?.phone : null}
                          $customStyles={{ flexGrow: 1 }}
                        />
                      </Flex>
                    </Box>
                  </Grid>
                  <Separator />
                  <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                        Address
                      </H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                        Edit Provider Address.
                      </Subtext>
                    </Box>
                    <Box>
                      <Flex sx={{ gap: "1rem" }}>
                        <TextInput
                          name="address[line1]"
                          label="Address 1"
                          value={values?.address?.line1}
                          onChange={(val: any) => setFieldValue("address.line1", val)}
                          $customStyles={{ flexGrow: 2 }}
                        />
                        <TextInput
                          name="address[line2]"
                          label="Address 2"
                          value={values?.address?.line2}
                          onChange={(val: any) => setFieldValue("address.line2", val)}
                          $customStyles={{ flexGrow: 1 }}
                        />
                      </Flex>
                      <Flex sx={{ gap: "1rem" }}>
                        <TextInput name="address[city]" label="City" value={values?.address?.city} onChange={(val: any) => setFieldValue("address.city", val)} $customStyles={{ width: "33.3333%" }} />
                        <SelectInput
                          name="address[state]"
                          label="State"
                          value={values?.address?.state}
                          options={countryStates}
                          onChange={(val: any) => setFieldValue("address.state", val)}
                          clearable
                          $customStyles={{ width: "33.3333%" }}
                        />
                        <TextInput
                          name="address[postalCode]"
                          label="Zip"
                          value={values?.address?.postalCode}
                          onChange={(val: any) => setFieldValue("address.postalCode", val)}
                          $customStyles={{ width: "33.3333%" }}
                        />
                      </Flex>
                    </Box>
                  </Grid>
                  <Separator />
                  <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                        Email
                      </H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                        Edit preferred emails for the provider.
                      </Subtext>
                    </Box>
                    <Box>
                      <Grid gap={3} columns={[1, 2]}>
                        <TextInput
                          type="email"
                          name="contactEmail"
                          label="Contact Email"
                          value={values?.contactEmail}
                          onChange={(val: any) => setFieldValue("contactEmail", val)}
                          $errors={errors?.contactEmail && submitCount > 0 ? errors?.contactEmail : null}
                        />
                        <TextInput
                          type="email"
                          name="billingEmail"
                          label="Billing Email"
                          value={values?.billingEmail}
                          onChange={(val: any) => setFieldValue("billingEmail", val)}
                          $errors={errors?.billingEmail && submitCount > 0 ? errors?.billingEmail : null}
                        />
                      </Grid>
                    </Box>
                  </Grid>
                  <Separator />
                  <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                        Depots
                      </H5>
                      <Subtext marginTop={0} marginBottom={30} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                        Edit depots associated with this provider.
                      </Subtext>
                      <CreateDepot includeButton provider={provider} afterCreate={(val: any) => navigate(`/providers/${provider.id}/depots/${val?.id}`)} />
                    </Box>
                    <Box>
                      <TableData
                        api={getDepots}
                        apiVariables={{ provider, includeDeleted: true }}
                        enableSearch={false}
                        refresh={refreshDepots}
                        pagingIfPaged={true}
                        nothingFoundText="There are currently no depots associated with this provider."
                        columns={[
                          createColumn(
                            "name",
                            "Depot Name",
                          (row) => row?.name,
                          (info) => {
                              const record = info.row.original;
                              const isInactive = Boolean(record?.deletedAt);
                              return (
                                <Box
                                  sx={{
                                    color: "#000",
                                    cursor: isInactive ? "default" : "pointer",
                                    opacity: isInactive ? 0.65 : 1,
                                  }}
                                  onClick={() => {
                                    if (!isInactive) {
                                      navigate(`/providers/${provider.id}/depots/${record?.id}`);
                                    }
                                  }}
                                >
                                  {info.getValue()}
                                </Box>
                              );
                            }
                          ),
                          createColumn(
                            "status",
                            "Status",
                            (row) => (row?.deletedAt ? "Inactive" : "Active"),
                            (info) => (
                              <Badge as="span" variant={info.getValue() === "Active" ? "success" : "danger"}>
                                {info.getValue()}
                              </Badge>
                            ),
                            { meta: { style: { width: "120px" } } }
                          ),
                          createColumn("serviceArea.name", "Service Area", (row) => row?.serviceArea?.name),
                          createColumn(
                            "preferred",
                            "Preferred",
                            (row) => row.preferred,
                            (info) => (info.getValue() ? "Yes" : "No")
                          ),
                          createColumn("contactEmail", "Contact Email", (row) => row?.contactEmail),
                          createColumn(
                            "actions",
                            "",
                            () => "actions",
                            (info) => {
                              const record = info.row.original;
                              const isInactive = Boolean(record?.deletedAt);
                              const items = [
                                ...(!isInactive
                                  ? [
                                      {
                                        label: "Edit",
                                        onClick: () => navigate(`/providers/${provider.id}/depots/${record?.id}`),
                                      },
                                    ]
                                  : []),
                                ...(isInactive
                                    ? [
                                        {
                                          label: "Reactivate",
                                          onClick: () => reactivateDepotModal(record),
                                          style: { color: themevals.colors.success },
                                      },
                                    ]
                                  : [
                                      {
                                        label: "Deactivate",
                                        onClick: () => deleteDepotModal(record),
                                        style: { color: "var(--theme-ui-colors-danger)" },
                                      },
                                    ]),
                              ];
                              return (
                                <Box>
                                  <DropDownMenu
                                    toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />}
                                    items={items}
                                  />
                                </Box>
                              );
                            },
                            { header: () => null, enableSorting: false, meta: { style: { width: "10px" } } }
                          ),
                        ]}
                      />
                    </Box>
                  </Grid>
                </Tab>
              </Tabs>
            </Form>
          )}
        </Formik>
      </Page>
      {confirmModal}
    </>
  );
}
